// IP合作二级单「音乐权益」外层包装组件：歌曲 Tab + Form.List。
// Form.List name="music_benefit" 绑定接口数组字段 IpCooperationMusicBenefitDto[]（每首歌一个元素），
// tab 名「歌曲1/2/3」，支持增删（至少保留 1 首），全部 TabPane forceRender 常驻挂载保证提交一次校验全部歌曲。
// 参考范式：SupplierQuoteDrawer/Drawer.tsx（Tabs+Form.List+SFormItem 数组 namePath）+ ScoreTemplateTabs（editable-card 增删）。
// 每 tab 内容渲染单首歌曲组件 MusicBenefitTable（SFormItem 字段 + 每首歌一个 action/opinion）。

import React, { useEffect, useState } from 'react';
import {
  Form,
  Tabs,
  Button,
  Modal,
  Divider,
  Tooltip,
} from '@bedrock/components';
import MusicBenefitTable from './index';
import type { MusicBenefitTableProps } from './index';
import { Copy, Delete, Plus } from '@bedrock/icons-react';
import './index.less';

/** 至少一首歌曲校验（防御：UI 已用 closable 拦截删除最后一首，此处兜底） */
const atLeastOneSongValidator = (_rule: any, value?: unknown[]) => {
  if (!Array.isArray(value) || value.length === 0) {
    return Promise.reject(new Error('请至少添加一首歌曲'));
  }
  return Promise.resolve();
};

/** 外层包装组件 props = MusicBenefitTable props 去掉 songIndex（songIndex 由 Form.List field.name 注入） */
export type MusicBenefitSongsListProps = Omit<
  MusicBenefitTableProps,
  'songIndex'
>;

const MusicBenefitSongsList: React.FC<MusicBenefitSongsListProps> = (props) => {
  const { view } = props;
  const [activeKey, setActiveKey] = useState('0');
  const form = Form.useFormInstance();
  // 提交校验失败定位：Footer 提交失败时写入 { index, tick }，监听 tick 变化自动切到出错歌曲 Tab。
  // tick 保证同一首歌多次失败也能重复触发（useWatch 值不变不会触发 effect）。
  const musicErr = Form.useWatch('__music_benefit_error', form);
  useEffect(() => {
    if (musicErr?.index != null) setActiveKey(String(musicErr.index));
  }, [musicErr?.tick]);
  // 只读态（出价方案审批/外包版权方只读）：不展示歌曲 Tab 的「添加(+)」与「删除」按钮
  const readOnly = view === true;

  return (
    <>
      {/* 音乐权益校验失败歌曲定位（Footer 提交失败时 setFieldValue 写入；注册 Form.Item 保证 useWatch 感知） */}
      <Form.Item name="__music_benefit_error" hidden>
        <span />
      </Form.Item>
      <Form.List
        name="music_benefit"
        initialValue={[{}]}
        rules={[{ validator: atLeastOneSongValidator }]}
      >
        {(fields, { add, remove }) => {
          // editable-card 增删：add 追加新歌曲并切到新 tab；remove 删除后修正 activeKey
          const handleTabEdit = (
            targetKey: string | number,
            action: 'add' | 'remove'
          ) => {
            if (action === 'add') {
              const nextIndex = fields.length;
              add({});
              setActiveKey(String(nextIndex));
              return;
            }
            const index = Number(targetKey);
            // 删除最后一首被按钮 disabled 拦截，此处防御
            if (fields.length <= 1) return;
            remove(index);
            // 删除后 activeKey 修正：删除的是当前激活 tab → 切到前一首（无则第一首）；
            // 删除的是前面非激活 tab → 当前激活 tab 下标前移一位；删后面则保持不变。
            const remainingCount = fields.length - 1;
            const currentActive = Number(activeKey);
            let nextActive = currentActive;
            if (index < currentActive) {
              nextActive = currentActive - 1;
            } else if (index === currentActive) {
              nextActive = index > 0 ? index - 1 : 0;
            }
            setActiveKey(
              String(Math.min(Math.max(nextActive, 0), remainingCount - 1))
            );
          };

          // 复制当前歌曲 tab：深拷贝当前表单值（music_benefit[index]）并新增 tab，自动切到新 tab。
          // 深拷贝保证源/副本相互独立（改一个不影响另一个）；仅复制我方填写字段，
          // 版权方回复/意见走 negotiation_items 回显，不会随表单值带出。
          const handleTabCopy = (fieldName: string | number) => {
            const source = form.getFieldValue(['music_benefit', fieldName]);
            const copied =
              source == null ? {} : JSON.parse(JSON.stringify(source));
            const nextIndex = fields.length;
            add(copied);
            setActiveKey(String(nextIndex));
          };

          return (
            <Tabs
              activeKey={activeKey}
              onChange={setActiveKey}
              destroyInactiveTabPane={false}
              tabBarExtraContent={
                readOnly ? undefined : (
                  <>
                    <Divider type={'vertical'} />
                    <Tooltip title={'添加'}>
                      <Button
                        icon={<Plus />}
                        size={'small'}
                        type={'link'}
                        onClick={() => handleTabEdit('', 'add')}
                      />
                    </Tooltip>
                  </>
                )
              }
              className={'ip-cooperation-music-tab'}
            >
              {fields.map((field, index) => (
                <Tabs.TabPane
                  key={String(field.name)}
                  tab={
                    <div className={'flex items-center'}>
                      <span>音乐{index + 1}</span>
                      {!readOnly && (
                        <div className={'ml-2'}>
                          <Tooltip title={'复制'}>
                            <Button
                              icon={<Copy />}
                              size={'small'}
                              type={'link'}
                              className={
                                'text-[var(--text-color-2)] px-[3px] w-[22px] min-w-[22px]'
                              }
                              onClick={(e: React.MouseEvent) => {
                                // 阻止冒泡到 tab 切换：点击复制只复制，不激活/切换该 tab（新增后由 handleTabCopy 主动切到副本）
                                e.stopPropagation();
                                handleTabCopy(field.name);
                              }}
                            ></Button>
                          </Tooltip>
                          <Tooltip title={'删除'}>
                            <Button
                              icon={<Delete />}
                              size={'small'}
                              type={'link'}
                              className={'px-[3px] w-[22px] min-w-[22px]'}
                              danger
                              disabled={fields.length <= 1}
                              onClick={(e: React.MouseEvent) => {
                                // 阻止冒泡到 tab 切换：点击删除按钮只弹确认，不激活/切换该 tab
                                e.stopPropagation();
                                // 删除音乐 Tab 二次确认（防误删；删除最后一首由 disabled 拦截，此处仅 >1 首可触发）
                                Modal.confirm({
                                  title: `确认删除音乐${index + 1}？`,
                                  content:
                                    '删除后该音乐权益数据将被移除，确认删除吗？',
                                  okText: '删除',
                                  okButtonProps: { danger: true },
                                  onOk: () =>
                                    handleTabEdit(field.name, 'remove'),
                                });
                              }}
                            ></Button>
                          </Tooltip>
                        </div>
                      )}
                    </div>
                  }
                  forceRender
                >
                  <MusicBenefitTable songIndex={field.name} {...props} />
                </Tabs.TabPane>
              ))}
            </Tabs>
          );
        }}
      </Form.List>
    </>
  );
};

export default MusicBenefitSongsList;
